import "core-js/modules/es.array.push.js";
import "bezier-easing";
import { isToday, formattedPickupEadV3 } from "../../util/dates.mjs";
import "js-cookie";
import { formatHours, formatSpecialHours, formatPhone } from "../../util/store-mapping.mjs";
const fulfillmentGetters = {
  /**
   * getters.fulfillmentMessage: Primary message for display in fulfillment tiles
   * @param {*} state.shipping.selectedStore store location selected by user
   * @param {String} getters.computedSelectedShippingMethod 'SHIP' or 'PICKUP'
   * @param {Boolean} getters.computedSelectedSku.sellable If the sku that is selected can
   * be added to cart
   * @returns {String}
   */
  fulfillmentMessage(state, getters) {
    var _a;
    const {
      selectedStore
    } = state.shipping;
    let message = "";
    if (getters.computedSelectedShippingMethod === "PICKUP" && getters.computedSelectedSku.sellable && !state.shipping.noStoreAvailable) {
      if ((selectedStore == null ? void 0 : selectedStore.storeFulfillmentType) === "BOPUS") message = "In stock at ";
      if ((selectedStore == null ? void 0 : selectedStore.storeFulfillmentType) === "RSPU") message = "Free shipping to ";
      if ((selectedStore == null ? void 0 : selectedStore.storeFulfillmentType) === "UNFULFILLABLE") message = "Unavailable at ";
    } else if (getters.computedSelectedShippingMethod === "SHIP") {
      if (state.product.eligibleForShipping === true && ((_a = state.shipping.shippingAddress) == null ? void 0 : _a.postalCode)) message = "Ship to";
      if (state.product.eligibleForShipping !== true) message = "Ship to address is unavailable for this item.";
    }
    return message;
  },
  /**
   * getters.fulfillmentTileContent
   * @param {Boolean} state.product.anyOversizeCharges do any skus have an oversize charge
   * @param {Boolean}  state.shipping.hasNetworkError has a network error occured on request
   * @param {*} getters.shipTileContent rollup for ship tile component
   * @param {*} getters.pickupTileContent rollup for pickup tile component
   * @param {Boolean} getters.isFreeShipping whether sku qualifies for free shipping
   * @param {String} getters.fulfillmentMessage Primary message for fulfillment tiles component
   * @param {String} getters.computedSelectedShippingMethod 'PICKUP' or 'SHIP'
   * @returns {*} rollup of content props for fulfillment tiles
   */
  fulfillmentTileContent(state, getters) {
    const {
      shipTile,
      pickupTile
    } = state.buyBoxValidation;
    const errMessage = shipTile.hasError ? "Please select an available method" : "Please select an available method or check other stores";
    const showNetworkErrorMessage = state.shipping.hasNetworkError && getters.computedSelectedShippingMethod === "PICKUP";
    return {
      errors: {
        showError: shipTile.hasError || pickupTile.hasError,
        message: errMessage,
        shipError: shipTile.hasError,
        pickupError: pickupTile.hasError,
        showNetworkErrorMessage
      },
      isBike: getters.isBike,
      message: getters.fulfillmentMessage,
      shippingContextMessages: getters.shippingContextMessages,
      shipTileContent: getters.shipTileContent,
      pickupTileContent: getters.pickupTileContent
    };
  },
  /**
   * getters.customerType
   * @param {Boolean} getters.unifiedCustomerData.isMember is user a member
   * @param {Boolean} getters.unifiedCustomerData.isEmployee is user an employee
   * @returns {string} customer type of user
   */
  customerType(state, getters) {
    var _a, _b;
    if ((_a = getters.unifiedCustomerData) == null ? void 0 : _a.isEmployee) {
      return "E";
    }
    if ((_b = getters.unifiedCustomerData) == null ? void 0 : _b.isMember) {
      return "M";
    }
    return "N";
  },
  /**
   * getters.computedSelectedStoreId
   * @param {String | Number} state.shipping.selectedStore.storeId
   *   store id selected by user on this pageview
   * @param {String | Number} getters.unifiedCustomerData.preferredStore
   *  store id returned by session data
   * @returns {Number} universal store ID to use across all app logic
   */
  computedSelectedStoreId(state) {
    var _a, _b, _c;
    return parseInt(((_c = (_b = (_a = state.shipping) == null ? void 0 : _a.selectedStore) == null ? void 0 : _b.store) == null ? void 0 : _c.storeId) || state.userSelectedStoreData.storeId || state.user.preferredStore, 10);
  },
  /**
   * getters.eadRequestPayload
   * @param {*} state.shipping.shippingAddress address information
   * computed from geolocation and API response data
   * @param {String} getters.computedSelectedStoreId store ID for request
   * @returns {*} formatted object to send to EAD service
   */
  eadRequestPayload(state, getters) {
    var _a;
    const sku = (_a = getters.computedSelectedSku) == null ? void 0 : _a.skuId;
    const {
      shippingAddress
    } = state.shipping;
    return {
      orderId: "ORDER0001",
      orderLines: [{
        orderLineId: 1,
        sku,
        quantity: state.quantity,
        shipTo: {
          countryCode: "",
          state: "",
          city: "",
          postalCode: shippingAddress.postalCode,
          latitude: shippingAddress.latitude,
          longitude: shippingAddress.longitude
        },
        fulfillmentType: "ALL",
        storeNumber: getters.computedSelectedStoreId,
        fulfillmentOptions: []
      }],
      customerType: getters.customerType
    };
  },
  /**
   * getters.addressEditorProps
   * @param {*} state.shipping all shipping data
   * @param {String} getters.computedSelectedShippingMethod 'SHIP' or 'PICKUP'
   * @returns {*} rollup of props for the inline zip code editor
   */
  addressEditorProps(state) {
    var _a, _b;
    return {
      invalid: state.shipping.inlineEditorIsInvalid,
      initialZip: (_b = (_a = state.shipping) == null ? void 0 : _a.shippingAddress) == null ? void 0 : _b.postalCode,
      editorIsOpen: state.shipping.inlineShippingEditorOpen,
      geolocationError: state.shipping.hasGeolocationError
    };
  },
  hasShippingAddress(state) {
    var _a, _b, _c;
    return !!((_a = state.shipping.shippingAddress) == null ? void 0 : _a.postalCode) || !!((_b = state.shipping.shippingAddress) == null ? void 0 : _b.latitude) && !!((_c = state.shipping.shippingAddress) == null ? void 0 : _c.longitude);
  },
  pickupTileContent(state, getters) {
    var _a, _b, _c, _d;
    let cost = "";
    let timeline = "";
    const {
      hasError
    } = state.buyBoxValidation.pickupTile;
    const {
      selectedStore
    } = state.shipping;
    const method = (selectedStore == null ? void 0 : selectedStore.storeFulfillmentType) === "RSPU" ? "Ship to store" : "Pick up";
    const location = ((_a = state.userSelectedStoreData) == null ? void 0 : _a.storeName) ? `at ${state.userSelectedStoreData.storeName}` : "";
    const isAvailable = selectedStore == null ? void 0 : selectedStore.eligibleForPickUp;
    if (state.shipping.hasNetworkError) {
      return {
        cost: "FREE",
        isAvailable: false,
        location,
        method: "Pick up"
      };
    }
    if (getters.isPickupRestricted) {
      return {
        isAvailable: false,
        notOffered: true,
        timeline: "Not offered",
        method: "Pick up"
      };
    }
    if (getters.isPreorder) {
      return {
        cost: "FREE",
        hasError,
        isAvailable: true,
        location,
        method: "Ship to store",
        timeline: ""
      };
    }
    if ((selectedStore == null ? void 0 : selectedStore.storeFulfillmentType) === "RSPU") {
      const isLegacy = (_b = selectedStore == null ? void 0 : selectedStore.estimatedArrivalDate) == null ? void 0 : _b.includes("displays in cart");
      const getEstimatedArrivalDate = (selectedStore == null ? void 0 : selectedStore.estimatedArrivalDate) === "-1" ? "" : `Ready ${selectedStore == null ? void 0 : selectedStore.estimatedArrivalDate}`;
      timeline = isLegacy ? selectedStore == null ? void 0 : selectedStore.estimatedArrivalDate : getEstimatedArrivalDate;
    } else timeline = selectedStore == null ? void 0 : selectedStore.estimatedArrivalDate;
    if (isAvailable && location) {
      const costBOPUS = (_d = (_c = getters.appPropsMap) == null ? void 0 : _c.textStrings) == null ? void 0 : _d.pickupTileCostBOPUS;
      cost = (selectedStore == null ? void 0 : selectedStore.storeFulfillmentType) === "BOPUS" && costBOPUS ? costBOPUS : "FREE";
    }
    return {
      cost,
      hasError,
      isAvailable,
      location,
      method,
      timeline
    };
  },
  shipTileContent(state, getters) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const {
      hasError
    } = state.buyBoxValidation.shipTile;
    const method = "Ship to address";
    const location = ((_b = (_a = state.shipping) == null ? void 0 : _a.shippingAddress) == null ? void 0 : _b.postalCode) ? `to ${(_d = (_c = state.shipping) == null ? void 0 : _c.shippingAddress) == null ? void 0 : _d.postalCode}` : "";
    const cost = getters.oversizeMessage ? "" : "FREE";
    const footnote = getters.isFreeShipping || getters.oversizeMessage ? "" : `$${getters.appPropsMap.textStrings.shippingThreshold} minimum`;
    if (getters.isPreorder) {
      return {
        location,
        method,
        notOffered: false,
        timeline: "",
        isAvailable: true,
        cost,
        footnote
      };
    }
    if (getters.computedIsPickupOnly) {
      return {
        location: "",
        method,
        notOffered: true,
        timeline: "Not offered",
        isAvailable: false
      };
    }
    if (state.shipping.hasNetworkError) {
      return {
        location,
        method: "Ship to address",
        cost,
        footnote,
        isAvailable: true
      };
    }
    if (getters.outOfStockShip) {
      return {
        location: "",
        method: "Ship to address",
        timeline: "Out of stock",
        isAvailable: false
      };
    }
    let timeline = "";
    if ((_e = state.shipping.shippingAvailability) == null ? void 0 : _e.estimatedArrivalDate) {
      timeline = `By ${state.shipping.shippingAvailability.estimatedArrivalDate}`;
    } else if (((_f = state.shipping) == null ? void 0 : _f.hasNetworkError) === true) timeline = "Delivery date shown in cart";
    return {
      method: "Ship to address",
      notOffered: getters.computedIsPickupOnly,
      timeline,
      hasError,
      location,
      cost,
      footnote,
      isAvailable: ((_h = (_g = state.shipping) == null ? void 0 : _g.shippingAvailability) == null ? void 0 : _h.isAvailable) === true || null
    };
  },
  shippingContextMessages(state, getters) {
    var _a, _b;
    const show = getters.computedSelectedShippingMethod === "SHIP";
    const shippingContextMessageList = [];
    if (getters.computedIsPickupOnly) {
      shippingContextMessageList.push({
        show,
        message: "<strong>Pick up only</strong>&mdash;Shipping not offered for this item"
      });
    } else if (getters.shippingRestrictionSummaryMessage || ((_b = (_a = state.shipping) == null ? void 0 : _a.shippingAvailability) == null ? void 0 : _b.shippingRestrictionMessage)) {
      shippingContextMessageList.push({
        show,
        message: `${getters.shippingRestrictionSummaryMessage || "Shipping restrictions apply"}. `,
        linkEvent: "shippingInfoClick"
      });
    }
    if (getters.oversizeMessage) {
      shippingContextMessageList.push({
        show,
        message: `${getters.oversizeMessage}. `,
        linkEvent: "oversizeInfoClick"
      });
    }
    if (getters.isPreorder) {
      shippingContextMessageList.push({
        show: true,
        message: "Most pre-orders ship within 30 days. ",
        linkEvent: "preorderBannerClick"
      });
    }
    return shippingContextMessageList.length ? shippingContextMessageList : [{
      show: false
    }];
  },
  pickupContextMessage(state, getters) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
    if (!((_a = state.userSelectedStoreData) == null ? void 0 : _a.storeName)) {
      return {
        showEdit: false
      };
    }
    if (getters.isPickupRestricted) {
      return {
        available: false,
        boldMessage: "Online Only",
        message: "—Not available in stores",
        showEdit: false
      };
    }
    if (((_c = (_b = state.shipping) == null ? void 0 : _b.selectedStore) == null ? void 0 : _c.storeFulfillmentType) === "BOPUS") {
      return {
        available: true,
        boldMessage: "In Stock",
        message: ` at ${state.shipping.selectedStore.store.storeName}`,
        showEdit: true
      };
    }
    if (getters.isAvailableNearby.available) {
      return {
        available: true,
        boldMessage: "In stock nearby",
        message: ` at ${getters.isAvailableNearby.storeName} (${getters.isAvailableNearby.distance} miles away)`,
        showEdit: true
      };
    }
    if (!getters.isAvailableNearby.available && getters.isAvailableNearby.hasResults && getters.computedSelectedShippingMethod === "PICKUP") {
      return {
        available: false,
        message: `Not available in store near ${((_g = (_f = (_e = (_d = state.shipping) == null ? void 0 : _d.selectedStore) == null ? void 0 : _e.store) == null ? void 0 : _f.storeAddress) == null ? void 0 : _g.zipCode) || "you"}`
      };
    }
    if (((_i = (_h = state.shipping) == null ? void 0 : _h.selectedStore) == null ? void 0 : _i.store) && ((_k = (_j = state.shipping) == null ? void 0 : _j.selectedStore) == null ? void 0 : _k.storeFulfillmentType) !== "BOPUS" && getters.computedSelectedShippingMethod === "PICKUP") {
      return {
        available: false,
        boldMessage: "Out of stock",
        message: ` at ${state.shipping.selectedStore.store.storeName}`,
        showEdit: true
      };
    }
    return {
      showEdit: false
    };
  },
  allSkusHavePickupRestriction(state, getters) {
    return getters.sellableSkus.filter((sku) => {
      var _a;
      return (_a = sku.shippingRestrictions) == null ? void 0 : _a.storePickupRestriction;
    }).length === getters.sellableSkus.length;
  },
  isPickupRestricted(state, getters) {
    var _a, _b;
    return getters.allSkusHavePickupRestriction || !!((_b = (_a = getters.computedSelectedSku) == null ? void 0 : _a.shippingRestrictions) == null ? void 0 : _b.storePickupRestriction);
  },
  transformedBlueYonderStoreData(state) {
    const getEstimatedArrivalDate = (storeEntry) => {
      let {
        estimatedArrivalDate
      } = storeEntry;
      if (estimatedArrivalDate === "-1") return estimatedArrivalDate;
      estimatedArrivalDate = storeEntry.fulfillmentServiceLevel ? formattedPickupEadV3(new Date(estimatedArrivalDate), storeEntry.store.timeZoneId) : estimatedArrivalDate;
      return estimatedArrivalDate;
    };
    return state.storeData.blueYonderResponseData.map((storeEntry) => ({
      eligibleForPickUp: !!storeEntry.fulfillmentServiceLevel,
      inventoryStatus: storeEntry.fulfillmentType !== "PICK" ? "NOT_AVAILABLE" : "AVAILABLE",
      estimatedArrivalDate: getEstimatedArrivalDate(storeEntry),
      estimatedArrivalDateTimestamp: storeEntry.fulfillmentServiceLevel ? storeEntry.estimatedArrivalDate : null,
      store: {
        storeId: storeEntry.store.storeNumber,
        storeName: storeEntry.store.storeDisplayName,
        distanceMessage: `${storeEntry.store.distance.toFixed(2)} miles`,
        enabledForCurbside: storeEntry.store.enabledForCurbside,
        enabledForInStore: storeEntry.store.enabledForInStore,
        curbsideHours: formatHours(storeEntry.store.retailStoreHoursEvents.find((event) => event.hoursType === "CurbsideHours")),
        storeHours: formatHours(storeEntry.store.retailStoreHoursEvents.find((event) => event.hoursType === "NormalHours")),
        specialStoreHours: formatSpecialHours(storeEntry.store.retailStoreHoursEvents.filter((event) => event.hoursType === "SpecialHours")),
        storeAddress: {
          line1: storeEntry.store.address1,
          city: storeEntry.store.city,
          state: storeEntry.store.state,
          zipCode: storeEntry.store.zip
        },
        phoneNumber: formatPhone(storeEntry.store.phone)
      },
      storeFulfillmentType: storeEntry.fulfillmentServiceLevel ? storeEntry.fulfillmentServiceLevel : "UNFULFILLABLE"
    }));
  },
  /**
  *  getters.blueYonderStoreModalAnalytics
  *  Analytics data for the BlueYonder EAD service responses delivered to the store pickup modal
  * Spec here: https://confluence.rei.com/display/WA/PDP+-+BOPUS+%28Find+in+Store%29+modal
  * @param {*} state
  * @returns {Object} object with data expected by analytics about the store availability response
  */
  blueYonderStoreModalAnalytics(state) {
    const {
      blueYonderResponseData: stores,
      searchTerm,
      geoLocation = {}
    } = state.storeData;
    const geoLocationQueryParam = `latitude=${geoLocation.latitude}&longitude=${geoLocation.longitude}`;
    const curbside = stores.map((info) => info.store.enabledForCurbside === true ? 1 : 0);
    const pickUpInStore_distance = stores.map((info) => info.store.distance.toFixed(2)).join("_");
    const pickUpInStore_status = stores.map((info) => {
      if (info.fulfillmentServiceLevel === "BOPUS") {
        const ead = new Date(info.estimatedArrivalDate);
        const now = /* @__PURE__ */ new Date();
        return isToday(now, ead) ? "1" : "2";
      }
      return 0;
    }).join("_");
    return {
      linkName: state.storeData.linkName,
      clickData: {
        pageName: `${state.analytics.pageName}_find in store modal`,
        pickUpInStore_curbsideStatus: curbside.join("_"),
        // underscore seperated list of curbside status
        pickUpInStore_distance,
        // underscore seperated list of distance in miles of stores returned
        pickupInStore_locationQuery: searchTerm || geoLocationQueryParam,
        pickUpInStore_numStoreCurbside: curbside.filter((status) => status === 1).length,
        pickUpInStore_numStoreCurbsideUnavailable: curbside.filter((sts) => sts === 0).length,
        pickUpInStore_numStores: state.storeData.blueYonderResponseData.length,
        pickUpInStore_numStoresBopus: stores.filter((info) => info.fulfillmentServiceLevel === "BOPUS").length,
        pickUpInStore_numStoreRspu: stores.filter((info) => info.fulfillmentServiceLevel === "RSPU").length,
        pickUpInStore_status,
        // underscore seperated list of BOPUS EAD: 1=TODAY, 2=TOMORROW, 0=NA
        pickUpInStore_storeId: stores.map((info) => info.store.storeNumber).join("_")
        // underscore seperated list of store ID numbers
      }
    };
  },
  blueYonderEndpoint(state, getters) {
    const {
      searchTerm
    } = state.storeData;
    const latitude = state.storeData.geoLocation.latitude || state.shipping.shippingAddress.latitude;
    const longitude = state.storeData.geoLocation.longitude || state.shipping.shippingAddress.longitude;
    const blueYonderPath = getters.appPropsMap.endpoints.storeModalFulfillmentDeliveryDate;
    const location = searchTerm ? `search/${encodeURIComponent(searchTerm.replace(/,\s+/g, ","))}` : `search/lat/${latitude}/long/${longitude}`;
    return `${blueYonderPath}${getters.computedSelectedSku.skuId}/${location}?qty=${state.quantity}&customerType=${getters.customerType}`;
  },
  isAvailableNearby(state) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const availableStore = (_c = (_b = (_a = state.storeData) == null ? void 0 : _a.blueYonderResponseData) == null ? void 0 : _b.find) == null ? void 0 : _c.call(_b, (store) => store.fulfillmentServiceLevel === "BOPUS");
    return {
      hasResults: !!((_e = (_d = state.storeData) == null ? void 0 : _d.blueYonderResponseData) == null ? void 0 : _e.length),
      available: !!availableStore,
      distance: ((_f = availableStore == null ? void 0 : availableStore.store) == null ? void 0 : _f.distance.toFixed(1)) || null,
      storeName: (_g = availableStore == null ? void 0 : availableStore.store) == null ? void 0 : _g.storeDisplayName,
      storeZip: (_h = availableStore == null ? void 0 : availableStore.store) == null ? void 0 : _h.zip
    };
  },
  computedIsPickupOnly(state, getters) {
    var _a, _b;
    return ((_a = state.product) == null ? void 0 : _a.allSkusAreBopusOnly) || ((_b = getters.computedSelectedSku) == null ? void 0 : _b.bopusOnly) || !state.product.eligibleForShipping;
  },
  atcStickyFulfillmentLocation(state, getters) {
    var _a, _b, _c, _d;
    let location = "";
    if (getters.hasSelectedValidFulfillment) {
      location = getters.computedSelectedShippingMethod === "SHIP" ? (_b = (_a = state.shipping) == null ? void 0 : _a.shippingAddress) == null ? void 0 : _b.postalCode : (_d = (_c = state.shipping.selectedStore) == null ? void 0 : _c.store) == null ? void 0 : _d.storeName;
    }
    return location;
  },
  atcStickyFulfillmentLabel(state, getters) {
    var _a, _b, _c, _d, _e, _f;
    let fulfillmentLabel = "";
    if (getters.computedSelectedShippingMethod === "PICKUP" && ((_a = getters.computedSelectedSku) == null ? void 0 : _a.sellable) && !state.shipping.noStoreAvailable) {
      if (((_c = (_b = state.shipping) == null ? void 0 : _b.selectedStore) == null ? void 0 : _c.storeFulfillmentType) === "BOPUS") fulfillmentLabel = "Pick up at:";
      if (((_e = (_d = state.shipping) == null ? void 0 : _d.selectedStore) == null ? void 0 : _e.storeFulfillmentType) === "RSPU") fulfillmentLabel = "Ship to:";
    } else if (getters.computedSelectedShippingMethod === "SHIP" && state.product.eligibleForShipping === true && ((_f = state.shipping.shippingAddress) == null ? void 0 : _f.postalCode)) {
      fulfillmentLabel = "Deliver to:";
    }
    return fulfillmentLabel;
  },
  hasSelectedValidFulfillmentStickyATC(state, getters) {
    return getters.hasValidPickup || getters.hasValidShip;
  },
  fulfillmentOrderIssues(state) {
    const issues = state.shipping.orderIssues || [];
    const pickIssue = issues.find((issue) => issue.fulfillmentType === "PICK");
    const shipIssue = issues.find((issue) => issue.fulfillmentType === "SHIP");
    return {
      pickIssue,
      shipIssue
    };
  },
  outOfStockShip(state, getters) {
    var _a, _b, _c;
    return !((_a = state.shipping.shippingAvailability) == null ? void 0 : _a.estimatedArrivalDate) && getters.appPropsMap.triageToggles.blueyonderShipAddToCart && ((_c = (_b = getters.fulfillmentOrderIssues) == null ? void 0 : _b.shipIssue) == null ? void 0 : _c.code) === 1023;
  },
  hasValidShip(state, getters) {
    return getters.computedSelectedShippingMethod === "SHIP" && !getters.computedIsPickupOnly && (!getters.outOfStockShip || getters.isPreorder);
  },
  hasValidPickup(state, getters) {
    var _a, _b, _c;
    const canPickup = !!((_a = state.shipping.selectedStore) == null ? void 0 : _a.eligibleForPickUp);
    const noStoreSelected = ((_c = (_b = state.shipping) == null ? void 0 : _b.selectedStore) == null ? void 0 : _c.store) === void 0;
    return getters.computedSelectedShippingMethod === "PICKUP" && // for preorders, bypass all EAD logic and allow add to cart
    (canPickup || noStoreSelected || getters.isPreorder);
  }
};
export {
  fulfillmentGetters as default
};
//# sourceMappingURL=fulfillmentGetters.mjs.map
